import type { SupplierSkuDto } from "@dishour/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { unwrap } from "./client.js";

const get = <T,>(path: string) => fetch(path, { credentials: "same-origin" }).then(unwrap<T>);
const send = <T,>(method: "POST" | "PATCH", path: string, body: unknown) =>
  fetch(path, {
    method,
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  }).then(unwrap<T>);

export const supplierSkusKey = ["supplier", "skus"] as const;

export const useSupplierSkus = () =>
  useQuery({ queryKey: supplierSkusKey, queryFn: () => get<SupplierSkuDto[]>("/api/supplier/skus") });

export type CreateSupplierSkuBody = {
  nameKo: string;
  nameEn: string | null;
  unit: string;
  packSize: string | null;
  isDivisible: boolean;
  qtyStep: string;
  unitPrice: string;
  aliases: string[];
};

export type UpdateSupplierSkuBody = Partial<Omit<CreateSupplierSkuBody, "aliases">> & { isActive?: boolean };

function useSupplierSkuMutation<TVars>(fn: (vars: TVars) => Promise<SupplierSkuDto>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: supplierSkusKey }),
  });
}

export const useCreateSupplierSku = () =>
  useSupplierSkuMutation((body: CreateSupplierSkuBody) => send<SupplierSkuDto>("POST", "/api/supplier/skus", body));

export const useUpdateSupplierSku = () =>
  useSupplierSkuMutation((vars: { id: string } & UpdateSupplierSkuBody) => {
    const { id, ...body } = vars;
    return send<SupplierSkuDto>("PATCH", `/api/supplier/skus/${id}`, body);
  });
